/**
 * F-JE-20, F-JE-21, F-JE-22, F-JE-26 — the staging area picked media and voice
 * recaps pass through on their way in.
 *
 * Staging exists because bytes arrive before anyone owns them. A picked file is
 * not the app's until it has been copied somewhere the app controls, and it is
 * not an *attachment* until the repository has verified its own copy. The gap
 * between those two moments is what this module manages, and every rule in it
 * is about that gap being survivable:
 *
 *  - The size bound is checked at preflight **and again during the copy**,
 *    because `byteCount` is what the source *claims*. A `File` handle whose
 *    backing file grows on disk keeps its declared size and streams more bytes
 *    than it promised, and a bound enforced only at preflight would let it.
 *  - The destination is created before the first byte, so every failure path
 *    has a concrete thing to delete rather than a name that may or may not
 *    exist yet.
 *  - A copy whose length disagrees with the declared size is `ioFailure` in
 *    both directions. A *short* copy is the dangerous one: it looks like a
 *    complete file and would be attached and hashed as if the missing bytes had
 *    never existed.
 *  - Removal requires the target to be a direct child of this staging root with
 *    this area's prefix. That check is the reason a provider-supplied path
 *    cannot turn prune into arbitrary deletion.
 *
 * The two areas — picked media and voice recap — are separate instances with
 * separate prefixes and separate retention (12 vs 4), so pruning one can never
 * take the other's in-flight file.
 */

import type {
  JournalCleanupReport,
  JournalStagedMediaId,
  Milliseconds,
} from "@ff/domain";
import {
  JOURNAL_MEDIA_STAGING,
  JOURNAL_VOICE_STAGING,
  journalInstantFromMilliseconds,
  journalInstantToMilliseconds,
  sanitizeJournalExportExtension,
} from "@ff/domain";
import type {
  JournalDeletionResult,
  MediaByteSource,
  MediaStagingKind,
  MediaStagingPort,
  MediaStagingResult,
  StagedMediaFile,
} from "@ff/domain/ports";

import { journalPathJoin } from "../journal/files";
import { JOURNAL_STAGING_DIRECTORY, isSafeJournalName } from "../journal/layout";

import type { MediaFileStat, MediaFileStore, MediaFileWriter } from "./files";
import { MEDIA_COPY_CHUNK_BYTES, mediaAddCopied, mediaFixedChunks } from "./files";

/* ------------------------------------------------------------------------- *
 * Configuration
 * ------------------------------------------------------------------------- */

export interface MediaStagingConfiguration {
  readonly prefix: string;
  readonly staleAfter: Milliseconds;
  readonly maxRetained: number;
  readonly storageReserveBytes: number;
}

/**
 * The two areas' constants, taken from `@ff/domain` rather than restated.
 *
 * A second spelling of "12" here would be a second thing to change, and the one
 * that did not get changed would be the one pruning real files.
 */
export const MEDIA_STAGING_CONFIGURATIONS: Readonly<
  Record<MediaStagingKind, MediaStagingConfiguration>
> = Object.freeze({
  pickedMedia: Object.freeze({
    prefix: JOURNAL_MEDIA_STAGING.filenamePrefix,
    staleAfter: JOURNAL_MEDIA_STAGING.staleAfter,
    maxRetained: JOURNAL_MEDIA_STAGING.maxRetained,
    storageReserveBytes: JOURNAL_MEDIA_STAGING.storageReserveBytes,
  }),
  voiceRecap: Object.freeze({
    prefix: JOURNAL_VOICE_STAGING.filenamePrefix,
    staleAfter: JOURNAL_VOICE_STAGING.staleAfter,
    maxRetained: JOURNAL_VOICE_STAGING.maxRetained,
    storageReserveBytes: JOURNAL_VOICE_STAGING.storageReserveBytes,
  }),
});

export interface MediaStagingDependencies {
  readonly kind: MediaStagingKind;
  readonly files: MediaFileStore;
  /** Epoch milliseconds. */
  readonly now: () => number;
  /** A fresh uuid per staged file; lowercased and range-checked here. */
  readonly newId: () => string;
  /**
   * Free bytes, when the platform will say. `undefined` means it will not, and
   * that is deliberately **not** treated as "no room": refusing an import
   * because a quota API is missing would block a legitimate file on the absence
   * of telemetry rather than on the absence of space.
   */
  readonly availableBytes?: () => Promise<number | undefined>;
}

/* ------------------------------------------------------------------------- *
 * Path ownership
 * ------------------------------------------------------------------------- */

/**
 * Whether a path is this area's to delete: a direct child of the staging root
 * carrying this area's prefix (F-JE-22).
 *
 * "Direct child" is the load-bearing half. `Staging/journal-pick-x/../../Entries`
 * carries the prefix and is not a child of anything, and a prefix test alone
 * would happily follow it out of the Journal.
 */
export function isOwnedMediaStagingPath(prefix: string, path: string): boolean {
  const expected = `${JOURNAL_STAGING_DIRECTORY}/`;
  if (!path.startsWith(expected)) return false;
  const name = path.slice(expected.length);
  if (!isSafeJournalName(name)) return false;
  return name.startsWith(prefix);
}

/**
 * The staged id a filename asserts, or `undefined` when the name is not this
 * area's.
 *
 * Only the final extension is stripped, so an id is recovered from
 * `journal-pick-<uuid>.jpeg` and a name with no dot at all still yields its id.
 */
export function mediaStagedIdFromName(
  prefix: string,
  name: string,
): JournalStagedMediaId | undefined {
  if (!name.startsWith(prefix)) return undefined;
  const rest = name.slice(prefix.length);
  const dot = rest.lastIndexOf(".");
  const id = dot === -1 ? rest : rest.slice(0, dot);
  return id.length === 0 ? undefined : (id as JournalStagedMediaId);
}

/**
 * `safeExtension` from F-JE-20: lowercase, ASCII letters only, at most 12
 * characters, falling back to "data".
 *
 * The domain already publishes exactly that transform for export filenames, so
 * it is reused rather than restated — the two rules are the same rule, and a
 * private copy here would be the one that drifts.
 */
export function mediaStagedExtension(sourceName: string): string {
  const dot = sourceName.lastIndexOf(".");
  return sanitizeJournalExportExtension(dot === -1 ? "" : sourceName.slice(dot + 1));
}

/* ------------------------------------------------------------------------- *
 * The staging area
 * ------------------------------------------------------------------------- */

interface OwnedFile {
  readonly id: JournalStagedMediaId;
  readonly path: string;
  readonly stat: MediaFileStat;
}

export class MediaStaging implements MediaStagingPort {
  readonly kind: MediaStagingKind;

  readonly #configuration: MediaStagingConfiguration;
  readonly #files: MediaFileStore;
  readonly #now: () => number;
  readonly #newId: () => string;
  readonly #availableBytes: (() => Promise<number | undefined>) | undefined;

  constructor(dependencies: MediaStagingDependencies) {
    this.kind = dependencies.kind;
    this.#configuration = MEDIA_STAGING_CONFIGURATIONS[dependencies.kind];
    this.#files = dependencies.files;
    this.#now = dependencies.now;
    this.#newId = dependencies.newId;
    this.#availableBytes = dependencies.availableBytes;
  }

  async prepare(): Promise<JournalCleanupReport> {
    // Every owned file goes, including ones staged moments ago: `prepare` runs
    // once at startup, and anything it finds is by definition stranded by a
    // process that is no longer running.
    const owned = await this.#owned();
    return this.#removeAll(owned);
  }

  async stage(
    source: MediaByteSource,
    maximumBytes: number,
  ): Promise<MediaStagingResult> {
    const preflight = await this.#preflight(source, maximumBytes);
    if (preflight !== undefined) return preflight;

    const id = this.#newId().toLowerCase();
    // A minted id becomes a filename, and a filename becomes the ownership
    // check every later removal depends on. An id carrying a separator or a dot
    // would break the id-from-name recovery that prune and discard both use.
    if (!isSafeJournalName(id) || id.includes(".")) {
      return { kind: "rejected", error: "ioFailure" };
    }

    const name = `${this.#configuration.prefix}${id}.${mediaStagedExtension(source.name)}`;
    const path = journalPathJoin(JOURNAL_STAGING_DIRECTORY, name);

    let writer: MediaFileWriter;
    try {
      await this.#files.createDirectory(JOURNAL_STAGING_DIRECTORY);
      // The destination exists before the first byte, so every failure below
      // has a concrete file to delete rather than a name that may not be there.
      writer = await this.#files.create(path);
    } catch {
      await this.#deletePartial(path);
      return { kind: "rejected", error: "ioFailure" };
    }

    let copied = 0;
    try {
      for await (const chunk of mediaFixedChunks(
        source.chunks(),
        MEDIA_COPY_CHUNK_BYTES,
      )) {
        const next = mediaAddCopied(copied, chunk.byteLength);
        // An accumulator that stopped counting would hold the total below the
        // bound forever, so a total that cannot be represented is treated as
        // over the bound rather than as an unchanged one.
        if (next === undefined || next > maximumBytes) {
          return await this.#abandon(writer, path, "attachmentTooLarge");
        }
        await writer.write(chunk);
        copied = next;
      }
    } catch {
      return await this.#abandon(writer, path, "ioFailure");
    }

    if (copied !== source.byteCount) {
      // The source changed size under the copy. A short copy is the dangerous
      // direction: it looks complete, and it would be hashed and attached as
      // though the missing bytes had never existed.
      return await this.#abandon(writer, path, "ioFailure");
    }

    try {
      await writer.close();
    } catch {
      return await this.#abandon(writer, path, "ioFailure");
    }

    const file: StagedMediaFile = {
      id: id as JournalStagedMediaId,
      path,
      contentType: source.contentType,
      byteCount: copied,
      stagedAt: journalInstantFromMilliseconds(this.#now()),
    };

    // Retention is applied on the way out, preserving the file just staged —
    // the point of a maximum is that the (n+1)th arrival evicts the oldest, not
    // that the (n+1)th arrival is refused.
    await this.prune(file.id);
    return { kind: "staged", file };
  }

  async prune(protect?: JournalStagedMediaId): Promise<JournalCleanupReport> {
    const owned = await this.#owned();

    const doomed: OwnedFile[] = [];
    const survivors: OwnedFile[] = [];
    for (const candidate of owned) {
      if (candidate.id === protect) continue;
      const { stat } = candidate;
      if (stat.kind === "directory") {
        // Not a regular file. Nothing this area writes is a directory, so one
        // wearing our prefix is debris, and it is removed first (F-JE-22).
        doomed.push(candidate);
        continue;
      }
      if (stat.kind !== "file") continue;
      if (stat.modifiedAt === undefined) {
        // An unreadable date is not evidence of staleness. Keeping the file
        // costs bytes; removing it can cost an import that is still copying.
        continue;
      }
      const age = this.#now() - journalInstantToMilliseconds(stat.modifiedAt);
      if (age > this.#configuration.staleAfter) doomed.push(candidate);
      else survivors.push(candidate);
    }

    // The retention count is measured against everything still present after
    // the stale pass — the protected file and the undatable ones included,
    // because they occupy a retention slot even though neither can fill an
    // eviction one.
    const remaining = owned.length - doomed.length;
    let excess = remaining - this.#configuration.maxRetained;
    if (excess > 0) {
      const oldestFirst = [...survivors].sort(
        (left, right) => modifiedMillis(left) - modifiedMillis(right),
      );
      for (const victim of oldestFirst) {
        if (excess <= 0) break;
        doomed.push(victim);
        excess -= 1;
      }
    }

    return this.#removeAll(doomed);
  }

  async discard(file: StagedMediaFile): Promise<JournalDeletionResult> {
    if (!isOwnedMediaStagingPath(this.#configuration.prefix, file.path)) {
      return { kind: "failed", error: "unsafeRemovalPath" };
    }

    let existed: boolean;
    try {
      existed = await this.#files.fileExists(file.path);
      if (!existed) return { kind: "nothingToDelete" };
      await this.#files.remove(file.path);
    } catch {
      return { kind: "failed", error: "ioFailure" };
    }

    // A remove that reports success and leaves the file behind is a removal
    // that did not happen; reporting it as deleted would strand the bytes with
    // nobody left believing they exist.
    if (await this.#files.fileExists(file.path)) {
      return { kind: "failed", error: "deletionNotVerified" };
    }
    return { kind: "deleted" };
  }

  /* --- internals --------------------------------------------------------- */

  async #preflight(
    source: MediaByteSource,
    maximumBytes: number,
  ): Promise<MediaStagingResult | undefined> {
    if (!Number.isSafeInteger(maximumBytes) || maximumBytes <= 0) {
      return { kind: "rejected", error: "attachmentTooLarge" };
    }
    if (!Number.isSafeInteger(source.byteCount) || source.byteCount < 0) {
      // "Missing size" in F-JE-20: a source that cannot say how big it is
      // cannot be checked against a maximum, so it is not copied at all.
      return { kind: "rejected", error: "ioFailure" };
    }
    if (source.byteCount === 0) {
      return { kind: "rejected", error: "ioFailure" };
    }
    if (source.byteCount > maximumBytes) {
      return { kind: "rejected", error: "attachmentTooLarge" };
    }

    const probe = this.#availableBytes;
    if (probe !== undefined) {
      const available = await probe();
      if (available !== undefined) {
        const reserve = this.#configuration.storageReserveBytes;
        if (available <= reserve || source.byteCount > available - reserve) {
          // The reserve is headroom the Journal's own records need; consuming it
          // for media would make the next entry unwritable.
          return { kind: "rejected", error: "ioFailure" };
        }
      }
    }

    return undefined;
  }

  async #owned(): Promise<readonly OwnedFile[]> {
    await this.#files.createDirectory(JOURNAL_STAGING_DIRECTORY);
    const names = await this.#files.list(JOURNAL_STAGING_DIRECTORY);

    const owned: OwnedFile[] = [];
    for (const name of names) {
      const id = mediaStagedIdFromName(this.#configuration.prefix, name);
      if (id === undefined) continue;
      const path = journalPathJoin(JOURNAL_STAGING_DIRECTORY, name);
      // The ownership check is repeated against the assembled path rather than
      // trusted from the listing, so one substrate that returns a name with a
      // separator in it cannot widen what this area may delete.
      if (!isOwnedMediaStagingPath(this.#configuration.prefix, path)) continue;
      owned.push({ id, path, stat: await this.#files.stat(path) });
    }
    return owned;
  }

  async #removeAll(targets: readonly OwnedFile[]): Promise<JournalCleanupReport> {
    let removedFiles = 0;
    let removedBytes = 0;
    let failedRemovals = 0;

    for (const target of targets) {
      const bytes = target.stat.kind === "file" ? target.stat.byteCount : 0;
      try {
        if (target.stat.kind === "directory") {
          await this.#files.removeDirectory(target.path);
          if (await this.#files.directoryExists(target.path)) {
            failedRemovals += 1;
            continue;
          }
        } else {
          await this.#files.remove(target.path);
          if (await this.#files.fileExists(target.path)) {
            failedRemovals += 1;
            continue;
          }
        }
        removedFiles += 1;
        removedBytes += bytes;
      } catch {
        failedRemovals += 1;
      }
    }

    return { removedFiles, removedBytes, failedRemovals };
  }

  /**
   * Closes a failed copy and deletes what it managed to write.
   *
   * One helper rather than three inline pairs, because "any copy error deletes
   * the partial destination" (F-JE-20) is a single rule and three copies of it
   * are three chances for one branch to forget.
   */
  async #abandon(
    writer: MediaFileWriter,
    path: string,
    error: "attachmentTooLarge" | "ioFailure",
  ): Promise<MediaStagingResult> {
    try {
      await writer.abort();
    } catch {
      /* the destination is being deleted regardless */
    }
    await this.#deletePartial(path);
    return { kind: "rejected", error };
  }

  async #deletePartial(path: string): Promise<void> {
    // Best effort by construction: this runs on a path that has already failed,
    // and a throw here would replace the failure the caller is reporting with a
    // less informative one.
    try {
      await this.#files.remove(path);
    } catch {
      /* the sweep in `cleanup.ts` ages what this could not remove */
    }
  }
}

function modifiedMillis(file: OwnedFile): number {
  return file.stat.kind === "file" && file.stat.modifiedAt !== undefined
    ? journalInstantToMilliseconds(file.stat.modifiedAt)
    : 0;
}

export function createMediaStaging(
  dependencies: MediaStagingDependencies,
): MediaStaging {
  return new MediaStaging(dependencies);
}
